import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import Base64Tool from '../components/Base64Tool';
import FaqSection from '../components/Faq';
import { PageHero } from '../components/InfoSections';
import { useTheme } from '../theme';
import { softwareApplicationSchema, faqPageSchema, breadcrumbSchema, Faq } from '../lib/schema';

const FAQS: Faq[] = [
  { q: 'How do I encode text to Base64?', a: 'Paste or type your text into the input box above. The Base64-encoded result appears instantly on the right, ready to copy or download.' },
  { q: 'How does Base64 encoding work?', a: 'Base64 encoding groups your data into 6-bit chunks and maps each chunk to one of 64 printable characters (A–Z, a–z, 0–9, + and /), padding the end with "=" so the length is a multiple of four.' },
  { q: 'Why does encoded Base64 look longer than my text?', a: 'Base64 represents every 3 bytes of input as 4 characters, so encoded output is about 33% larger than the original data. This overhead is the trade-off for safe text transmission.' },
  { q: 'Can I encode a text file to Base64?', a: 'Yes. Use the Upload button to load a .txt file, and its contents will be encoded automatically.' },
];

export default function EncodePage() {
  const { themeClasses } = useTheme();
  return (
    <>
      <Seo
        title="Base64 Encode — Convert Text to Base64 Online | EasyBase64"
        description="Encode text to Base64 online for free. Paste your text and get the Base64-encoded result instantly. UTF-8 safe, private and runs entirely in your browser."
        path="/base64-encode"
        jsonLd={[
          softwareApplicationSchema({
            name: 'Base64 Encoder',
            description: 'Free online tool to encode text to Base64 instantly in the browser.',
            path: '/base64-encode',
          }),
          faqPageSchema(FAQS),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Base64 Encode', path: '/base64-encode' },
          ]),
        ]}
      />

      <PageHero
        title="Base64 Encode Online"
        subtitle={<>Convert any text to a Base64 string instantly. Type or paste below and copy the encoded result — need the reverse? Try the <Link to="/base64-decode" className="underline">Base64 decoder</Link>.</>}
      />

      <div className="mb-12">
        <Base64Tool initialMode="encode" variant="standard" />
      </div>

      <section className={`${themeClasses.infoCard} rounded-2xl p-5 sm:p-8 border mb-12`}>
        <h2 className={`text-2xl font-bold ${themeClasses.textPrimary} mb-4`}>How to encode to Base64</h2>
        <ol className={`list-decimal list-inside space-y-2 ${themeClasses.textSecondary} leading-relaxed`}>
          <li>Type or paste your text into the input field above.</li>
          <li>The Base64 output is generated automatically as you type.</li>
          <li>Click <strong>Copy</strong> to copy the result, or <strong>Save</strong> to download it as a text file.</li>
          <li>Need a token- or URL-friendly result? Use the <Link to="/url-safe-base64" className="underline">URL-safe Base64</Link> tool instead.</li>
        </ol>
      </section>

      <FaqSection title="Base64 encoding FAQ" faqs={FAQS} />
    </>
  );
}
